import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
} from '@material-ui/core'
import { Add, Delete, Edit, Check, Close } from '@material-ui/icons'
import useAmenitiesApi from '../../hooks/amenities/useAmenitiesApi'

// 15 default types are seeded into the DB by the Phase 1 migration. The
// predefined-chips section that used to live here was removed; admin-curated
// types come from the AmenityType API directly. Custom types still get created
// here via the "Agregar tipo" input.
export default function AmenityTypesManager({ open, onClose }) {
  const [newTypeName, setNewTypeName] = useState('')
  const [newTypeIcon, setNewTypeIcon] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // Inline-edit state — null when nothing is being edited.
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editIcon, setEditIcon] = useState('')

  const [{ amenityTypesState }, methods] = useAmenitiesApi()

  useEffect(() => {
    if (open) {
      methods.getAmenityTypes()
    }
  }, [open])

  const handleAddType = async () => {
    const trimmed = newTypeName.trim()
    if (!trimmed) return
    setIsCreating(true)
    try {
      await methods.createAmenityType({
        name: trimmed,
        icon: newTypeIcon.trim() || undefined,
      })
      setNewTypeName('')
      setNewTypeIcon('')
    } catch (error) {
      // The hook surfaces a toast already.
    } finally {
      setIsCreating(false)
    }
  }

  const startEdit = (type) => {
    setEditingId(type.id)
    setEditName(type.name || '')
    setEditIcon(type.icon || '')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditName('')
    setEditIcon('')
  }

  const commitEdit = async () => {
    const trimmedName = editName.trim()
    if (!trimmedName) return
    try {
      await methods.updateAmenityType(editingId, {
        name: trimmedName,
        icon: editIcon.trim() || null,
      })
      cancelEdit()
    } catch (e) {
      // Hook handles the toast.
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Gestionar Tipos de Amenities</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Agregar tipo:
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              style={{ flex: 1 }}
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="Nombre"
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAddType()
              }}
            />
            <TextField
              style={{ width: 80 }}
              value={newTypeIcon}
              onChange={(e) => setNewTypeIcon(e.target.value)}
              placeholder="🏊"
              inputProps={{ maxLength: 4 }}
            />
            <IconButton
              color="primary"
              onClick={handleAddType}
              disabled={!newTypeName.trim() || isCreating}
            >
              <Add />
            </IconButton>
          </Box>
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Tipos existentes:
        </Typography>
        <List>
          {amenityTypesState.amenityTypes.map((type) => {
            const isEditing = editingId === type.id
            return (
              <ListItem key={type.id} divider>
                {isEditing ? (
                  <Box display="flex" flex={1} gap={1} alignItems="center">
                    <TextField
                      style={{ flex: 1 }}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                    <TextField
                      style={{ width: 80 }}
                      value={editIcon}
                      onChange={(e) => setEditIcon(e.target.value)}
                      placeholder="🏊"
                      inputProps={{ maxLength: 4 }}
                    />
                  </Box>
                ) : (
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <span
                          style={{ fontSize: '1.2rem', marginRight: '8px' }}
                        >
                          {type.icon || '🏢'}
                        </span>
                        {type.name}
                      </Box>
                    }
                  />
                )}
                <ListItemSecondaryAction>
                  {isEditing ? (
                    <>
                      <IconButton onClick={commitEdit} color="primary">
                        <Check />
                      </IconButton>
                      <IconButton onClick={cancelEdit}>
                        <Close />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={() => startEdit(type)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        color="secondary"
                        onClick={() => methods.deleteAmenityType(type.id)}
                      >
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            )
          })}
          {amenityTypesState.amenityTypes.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No hay tipos de amenities"
                secondary="Agrega el primer tipo usando el formulario de arriba"
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
